import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { formatDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useMasterList } from "@/hooks/useMasterData";
import { BROKER_SCOPES, isArchivedBroker } from "@/lib/brokerScopes";
import MasterSelect from "@/components/brokers/MasterSelect";
import BrokerFormFields from "@/components/brokers/BrokerFormFields";
import TonalBadge from "@/components/TonalBadge";

function CreateBrokerDialog({ open, onClose, defaultTypeLabel }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: types = [] } = useMasterList("broker_type");
  const [form, setForm] = useState({});
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  useEffect(() => {
    if (open) {
      const def = types.find((t) => t.label === defaultTypeLabel);
      setForm(def ? { broker_type_id: def.id } : {});
    }
  }, [open, defaultTypeLabel, types]);
  const create = useMutation({
    mutationFn: async () => (await api.post("/brokers", form)).data,
    onSuccess: (b) => {
      qc.invalidateQueries({ queryKey: ["brokers"] });
      toast.success("Broker added");
      onClose();
      navigate(`/brokers/${b.id}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="create-broker-dialog">
        <DialogHeader><DialogTitle>Add Broker</DialogTitle></DialogHeader>
        <BrokerFormFields form={form} set={set} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => create.mutate()} disabled={!form.broker_name || create.isPending} className="bg-slate-900 hover:bg-slate-800" data-testid="create-broker-submit">Create broker</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Chip({ children, className = "" }) {
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-700 border border-slate-200 ${className}`}>{children}</span>;
}

function CoverageCell({ cities }) {
  const [open, setOpen] = useState(false);
  if (!cities || cities.length === 0) return <span className="text-slate-300">—</span>;
  if (cities.length <= 4) {
    return <div className="flex flex-wrap gap-1">{cities.map((c) => <Chip key={c.id}>{c.name}</Chip>)}</div>;
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="flex flex-wrap gap-1 items-center">
        {cities.slice(0, 3).map((c) => <Chip key={c.id}>{c.name}</Chip>)}
        <PopoverTrigger asChild>
          <button type="button" onClick={(e) => e.stopPropagation()} className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-900 text-white hover:bg-slate-700" data-testid="coverage-more">+{cities.length - 3}</button>
        </PopoverTrigger>
      </div>
      <PopoverContent align="start" className="w-56 p-0" onClick={(e) => e.stopPropagation()} data-testid="coverage-popover">
        <div className="px-3 py-2 border-b border-slate-100 text-[11px] font-semibold uppercase tracking-wider text-slate-600">Coverage · {cities.length}</div>
        <div className="max-h-56 overflow-y-auto p-2 flex flex-wrap gap-1">
          {cities.map((c) => <Chip key={c.id}>{c.name}</Chip>)}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function SharedAssetsCell({ brokerId, preview, count }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { data: all = [], isLoading } = useQuery({
    queryKey: ["broker-shares-popup", brokerId],
    queryFn: async () => (await api.get("/brokers/shares", { params: { broker_id: brokerId } })).data,
    enabled: open,
  });
  if (!count) return <span className="text-slate-300">—</span>;
  const goAsset = (e, id) => { e.stopPropagation(); navigate(`/assets/${id}`); };
  return (
    <div className="space-y-1">
      {(preview || []).map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <button type="button" onClick={(e) => goAsset(e, p.asset_id)} className="text-sm text-slate-800 hover:underline truncate max-w-[150px] text-left" data-testid={`shared-asset-${p.asset_id}`}>{p.asset_name}</button>
          <TonalBadge listType="broker_share_status" label={p.share_status} className="!px-1.5 !py-0 shrink-0" />
        </div>
      ))}
      {count > 2 && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button type="button" onClick={(e) => e.stopPropagation()} className="text-[11px] font-semibold text-slate-600 hover:text-slate-900" data-testid={`shared-more-${brokerId}`}>+ {count - 2} more</button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-80 p-0" onClick={(e) => e.stopPropagation()} data-testid="shared-assets-popover">
            <div className="px-3 py-2 border-b border-slate-100 text-[11px] font-semibold uppercase tracking-wider text-slate-600">Shared Assets · {count}</div>
            <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
              {isLoading ? <div className="p-3 text-sm text-slate-400">Loading…</div> : all.map((s) => (
                <div key={s.id} className="px-3 py-2 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <button type="button" onClick={(e) => goAsset(e, s.asset_id)} className="text-sm text-slate-800 hover:underline truncate block text-left" data-testid={`popup-shared-asset-${s.asset_id}`}>{s.asset_name}</button>
                    <div className="text-[10px] text-slate-400 font-mono-num">{s.asset_code}</div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {s.exclusivity_type === "Exclusive" && <span className="text-[9px] font-bold uppercase text-amber-700 bg-amber-50 border border-amber-200 px-1 rounded" title="Exclusive">Excl</span>}
                    <TonalBadge listType="broker_share_status" label={s.share_status_label} className="!px-1.5 !py-0" />
                  </div>
                </div>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

function dateOnly(s) {
  const p = String(s).slice(0, 10).split("-");
  if (p.length !== 3) return null;
  return new Date(+p[0], +p[1] - 1, +p[2]);
}

function NextContactCell({ value }) {
  if (!value) return <span className="text-slate-300">—</span>;
  const d = dateOnly(value);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  let tone = "future";
  if (d) {
    if (d.getTime() === today.getTime()) tone = "today";
    else if (d.getTime() < today.getTime()) tone = "overdue";
  }
  const cls = tone === "today" ? "bg-amber-50 text-amber-700"
    : tone === "overdue" ? "bg-red-50 text-red-700"
    : "text-slate-600";
  return (
    <span className={`inline-flex font-mono-num text-sm px-2 py-0.5 rounded ${cls}`} data-testid={`next-contact-${tone}`}>
      {formatDate(value)}
    </span>
  );
}

export default function BrokersList({ scope = "retail" }) {
  const cfg = BROKER_SCOPES[scope] || BROKER_SCOPES.retail;
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showCreate, setShowCreate] = useState(false);
  const [filters, setFilters] = useState({});
  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
  const { data: allBrokers = [], isLoading } = useQuery({
    queryKey: ["brokers", params],
    queryFn: async () => (await api.get("/brokers", { params })).data,
  });

  // Scope filter (display-only): active lists exclude archived-status brokers; the
  // Archived view shows only Inactive/Blacklisted brokers (any type).
  const isArchivedView = scope === "archived";
  const brokers = useMemo(
    () => allBrokers.filter((b) => {
      const archived = isArchivedBroker(b.relationship_status_label);
      return isArchivedView ? archived : (cfg.match(b.broker_type_label) && !archived);
    }),
    [allBrokers, cfg, isArchivedView]
  );

  const brokerIds = useMemo(() => brokers.map((b) => b.id), [brokers]);
  const { data: summary = {} } = useQuery({
    queryKey: ["broker-directory-summary", brokerIds],
    queryFn: async () => (await api.post("/brokers/directory-summary", { broker_ids: brokerIds })).data,
    enabled: brokerIds.length > 0,
  });

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="brokers-list">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">{cfg.eyebrow}</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mt-1" data-testid="brokers-title">{cfg.title}</h1>
          <p className="text-sm text-slate-600 mt-1.5">{cfg.subtitle}</p>
        </div>
        {!isArchivedView && <Button onClick={() => setShowCreate(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="add-broker-btn"><Plus className="h-4 w-4 mr-1.5" /> Add Broker</Button>}
      </div>

      {/* Filters */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        <MasterSelect listType="broker_type" value={filters.broker_type_id} onChange={(v) => setF("broker_type_id", v)} placeholder="All Types" testId="filter-broker-type" />
        <MasterSelect listType="broker_relationship_status" value={filters.relationship_status_id} onChange={(v) => setF("relationship_status_id", v)} placeholder="All Statuses" testId="filter-rel-status" />
        <MasterSelect listType="city" value={filters.coverage_city_id} onChange={(v) => setF("coverage_city_id", v)} placeholder="Any Coverage City" testId="filter-coverage-city" />
        <MasterSelect listType="asset_category" value={filters.specialization_category_id} onChange={(v) => setF("specialization_category_id", v)} placeholder="Any Specialization" testId="filter-spec-cat" />
      </div>

      <div className="mt-5 bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="data-table w-full">
          <thead>
            <tr>
              <th className="label-eyebrow !text-slate-600">Broker</th>
              <th className="label-eyebrow !text-slate-600">Coverage</th>
              <th className="label-eyebrow !text-slate-600">Status</th>
              <th className="label-eyebrow !text-slate-600">Shared Assets</th>
              <th className="label-eyebrow !text-slate-600">Next Contact</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={5} className="text-center py-6 text-sm text-slate-500">Loading…</td></tr>
            ) : brokers.length === 0 ? (
              <tr><td colSpan={5} className="py-16 text-center" data-testid="brokers-empty">
                {Object.keys(params).length > 0 ? (
                  <div className="text-sm text-slate-500">No brokers match your filters.</div>
                ) : isArchivedView ? (
                  <>
                    <div className="font-display text-lg text-slate-700">No archived brokers</div>
                    <div className="text-sm text-slate-500 mt-1">Brokers set to Inactive or Blacklisted appear here.</div>
                  </>
                ) : (
                  <>
                    <div className="font-display text-lg text-slate-700">No brokers yet</div>
                    <div className="text-sm text-slate-500 mt-1">Add your first channel partner or sourcing broker.</div>
                    <Button className="mt-4 bg-slate-900 hover:bg-slate-800" onClick={() => setShowCreate(true)} data-testid="empty-state-add-broker">
                      <Plus className="h-4 w-4 mr-1.5" /> Add Broker
                    </Button>
                  </>
                )}
              </td></tr>
            ) : brokers.map((b) => {
              const s = summary[b.id] || {};
              return (
                <tr key={b.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer align-top" onClick={() => navigate(`/brokers/${b.id}`)} data-testid={`broker-row-${b.id}`}>
                  <td className="py-3">
                    <div className="text-sm font-semibold text-slate-900" data-testid="broker-name">{b.broker_name}</div>
                    <div className="text-[11px] text-slate-500">{b.firm_name || "—"}</div>
                  </td>
                  <td className="py-3"><CoverageCell cities={s.coverage_cities} /></td>
                  <td className="py-3"><TonalBadge listType="broker_relationship_status" label={b.relationship_status_label} /></td>
                  <td className="py-3"><SharedAssetsCell brokerId={b.id} preview={s.shared_assets} count={s.shared_count || 0} /></td>
                  <td className="py-3"><NextContactCell value={b.next_contact_date} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <CreateBrokerDialog open={showCreate} onClose={() => setShowCreate(false)} defaultTypeLabel={cfg.defaultTypeLabel} />
    </div>
  );
}
